import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Users, Search, Mail, Phone, Calendar, DollarSign, Download, X, Send, 
  UserPlus, Clock, Edit, Ticket, Trash2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Edit Guest Modal
const EditGuestModal = ({ guest, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: guest?.name || "",
    email: guest?.email || "",
    phone: guest?.phone || "",
    ticket_type: guest?.ticket_type || "",
    quantity: guest?.quantity || 1,
    total_amount: guest?.total_amount || 0,
    notes: guest?.notes || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Use the unified update endpoint for landing page bookings
      await axios.put(`${API}/admin/landing-page-bookings/${guest.id}`, formData);
      toast.success("Guest updated successfully");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to update guest");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]" data-testid="edit-guest-modal-title">
            Edit Guest
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              data-testid="edit-guest-name"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              data-testid="edit-guest-email"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Ticket Type</label>
              <input
                type="text"
                value={formData.ticket_type}
                onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Amount (C$)</label>
              <input
                type="number"
                step="0.01"
                value={formData.total_amount}
                onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="edit-guest-save">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Email Attendees Modal
const EmailAttendeesModal = ({ attendees, onClose, onSent }) => {
  const [formData, setFormData] = useState({ subject: "", message: "", schedule_time: "" });
  const [sending, setSending] = useState(false);
  const [jobProgress, setJobProgress] = useState(null);

  useEffect(() => {
    let interval;
    if (jobProgress && jobProgress.status !== "completed" && jobProgress.status !== "failed") {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/admin/email-jobs/${jobProgress.job_id}`);
          setJobProgress(response.data);
          if (response.data.status === "completed") {
            toast.success(`Email sent to ${response.data.sent} recipients`);
            setTimeout(() => onSent(), 1500);
          } else if (response.data.status === "failed") {
            toast.error(response.data.error || "Email job failed");
          }
        } catch (error) {
          console.error("Error polling job:", error);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [jobProgress, onSent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/attendees/send-email`, {
        attendee_ids: attendees.map(a => a.id),
        subject: formData.subject,
        message: formData.message,
        schedule_time: formData.schedule_time || null
      });
      if (response.data.async) {
        setJobProgress({ job_id: response.data.job_id, status: "pending", total: attendees.length, sent: 0, failed: 0 });
      } else {
        toast.success(`Email sent to ${response.data.sent} recipients`);
        onSent();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
      setSending(false);
    }
  };

  const progressPercent = jobProgress ? Math.round(((jobProgress.sent + jobProgress.failed) / jobProgress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0F0F0F] rounded-lg max-w-2xl w-full border border-[rgba(212,175,55,0.15)]">
        <div className="flex justify-between items-center p-4 border-b border-[rgba(212,175,55,0.15)]">
          <div>
            <h2 className="text-lg font-['Playfair_Display'] font-semibold text-[#F5F5F0]">Send Email</h2>
            <p className="text-[#A1A1AA] text-sm">{attendees.length} recipients</p>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="bg-[#1A1A1A] p-3 rounded max-h-24 overflow-y-auto">
            <p className="text-[#A1A1AA] text-xs mb-2">To:</p>
            <div className="flex flex-wrap gap-2">
              {attendees.slice(0, 10).map(a => (
                <span key={a.id} className="px-2 py-1 bg-[#0F0F0F] text-[#F5F5F0] text-xs rounded">{a.name}</span>
              ))}
              {attendees.length > 10 && <span className="px-2 py-1 text-[#A1A1AA] text-xs">+{attendees.length - 10} more</span>}
            </div>
          </div>
          <div>
            <label className="block text-[#A1A1AA] text-sm mb-2">Subject *</label>
            <input type="text" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-4 py-2 bg-[#1A1A1A] border border-[rgba(212,175,55,0.15)] rounded text-[#F5F5F0]" required />
          </div>
          <div>
            <label className="block text-[#A1A1AA] text-sm mb-2">Message *</label>
            <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-2 bg-[#1A1A1A] border border-[rgba(212,175,55,0.15)] rounded text-[#F5F5F0] resize-none" rows={6} required />
          </div>
          {jobProgress && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#A1A1AA]">{jobProgress.status === "processing" ? `Sending... ${jobProgress.sent}/${jobProgress.total}` : jobProgress.status}</span>
                <span className="text-[#D4AF37]">{progressPercent}%</span>
              </div>
              <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                <div className={`h-full transition-all duration-300 ${jobProgress.status === "completed" ? "bg-green-500" : "bg-[#D4AF37]"}`} style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[#A1A1AA]">Cancel</button>
            <button type="submit" disabled={sending || (jobProgress && !["completed","failed"].includes(jobProgress.status))}
              className="px-6 py-2 bg-[#D4AF37] text-[#050505] rounded hover:bg-[#C4A030] disabled:opacity-50 flex items-center gap-2">
              <Send className="w-4 h-4" />
              {sending && !jobProgress ? "Starting..." : "Send Now"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminGuestsPage = () => {
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [deletingGuest, setDeletingGuest] = useState(null);

  useEffect(() => {
    fetchAllBookings();
  }, []);

  const fetchAllBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-confirmed-bookings`);
      setAllBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGuest = async (guest) => {
    try {
      if (guest.type === "landing_page") {
        await axios.delete(`${API}/admin/landing-page-bookings/${guest.id}`);
      } else if (guest.type === "event") {
        await axios.delete(`${API}/admin/event-bookings/${guest.id}`);
      } else if (guest.type === "experience") {
        await axios.delete(`${API}/admin/experience-bookings/${guest.id}`);
      }
      toast.success("Guest deleted");
      setDeletingGuest(null);
      fetchAllBookings();
    } catch (error) {
      toast.error("Failed to delete guest");
    }
  };

  const filteredBookings = allBookings.filter(a => {
    const matchesSearch = 
      a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.booking_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (sourceFilter === "all") return matchesSearch;
    if (sourceFilter === "manual") return matchesSearch && a.source_type === "manual";
    return matchesSearch && a.source_slug === sourceFilter;
  });

  const totalRevenue = filteredBookings.reduce((sum, a) => sum + (a.total_amount || 0), 0);
  const totalGuests = filteredBookings.reduce((sum, a) => sum + (a.quantity || 1), 0);

  // Get unique sources for filter dropdown
  const uniqueSources = [...new Set(allBookings.map(b => b.source_display).filter(Boolean))];

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleExportCSV = () => {
    const csv = [
      ["Name", "Email", "Phone", "Event", "Ticket", "Qty", "Amount", "Ticket #", "Booking Code", "Source", "Date"].join(","),
      ...filteredBookings.map(a => [
        `"${a.name}"`, `"${a.email}"`, `"${a.phone || ""}"`, `"${a.event_title}"`,
        `"${a.ticket_type}"`, a.quantity, a.total_amount?.toFixed(2),
        (a.id || "").substring(0, 8).toUpperCase(),
        a.booking_code || "", `"${a.source_display}"`, a.created_at?.split("T")[0] || ""
      ].join(","))
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `guests_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success("Guest list exported");
  };

  const toggleSelectAttendee = (id) => {
    setSelectedAttendees(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const selectAllVisible = () => {
    setSelectedAttendees(filteredBookings.map(a => a.id));
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-guests">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              Guests (Legacy)
            </h1>
            <p className="text-[#A1A1AA] text-sm mt-1">
              Unified view of all confirmed bookings across landing pages, events, and experiences
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => selectedAttendees.length > 0 && setShowEmailModal(true)}
              disabled={selectedAttendees.length === 0}
              className="flex items-center gap-2 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded hover:bg-[#D4AF37]/10 disabled:opacity-50"
              data-testid="email-attendees-btn"
            >
              <Send className="w-4 h-4" />
              Email ({selectedAttendees.length})
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] rounded hover:text-[#D4AF37]"
              data-testid="export-guests-btn"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.15)]">
            <Users className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#F5F5F0]" data-testid="total-bookings-count">{filteredBookings.length}</p>
            <p className="text-[#A1A1AA] text-sm">Confirmed Bookings</p>
          </div>
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.15)]">
            <Ticket className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#F5F5F0]">{totalGuests}</p>
            <p className="text-[#A1A1AA] text-sm">Total Tickets</p>
          </div>
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.15)]">
            <DollarSign className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#F5F5F0]">C${totalRevenue.toLocaleString()}</p>
            <p className="text-[#A1A1AA] text-sm">Total Revenue</p>
          </div>
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.15)]">
            <Mail className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#F5F5F0]">{selectedAttendees.length}</p>
            <p className="text-[#A1A1AA] text-sm">Selected</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a1a1aa]" />
            <input
              type="text"
              placeholder="Search by name, email, or booking code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pl-10 rounded focus:border-[#D4AF37] focus:outline-none"
              data-testid="guest-search-input"
            />
          </div>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
            data-testid="guest-source-filter"
          >
            <option value="all">All Sources</option>
            <option value="manual">Manual Entries</option>
            {uniqueSources.filter(s => s !== "Manual" && s !== "Event" && s !== "Experience").map(source => (
              <option key={source} value={allBookings.find(b => b.source_display === source)?.source_slug || source}>
                {source}
              </option>
            ))}
          </select>
        </div>

        {/* Select All */}
        <div className="flex items-center gap-4">
          <button onClick={selectAllVisible} className="text-sm text-[#D4AF37] hover:underline">
            Select All Visible ({filteredBookings.length})
          </button>
          {selectedAttendees.length > 0 && (
            <button onClick={() => setSelectedAttendees([])} className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0]">
              Clear Selection
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12 bg-[#0F0F0F] rounded border border-[rgba(212,175,55,0.15)]">
            <Users className="w-12 h-12 text-[#a1a1aa] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">No confirmed bookings found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="guests-legacy-table">
              <thead>
                <tr className="border-b border-[rgba(212,175,55,0.15)]">
                  <th className="p-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedAttendees.length === filteredBookings.length && filteredBookings.length > 0}
                      onChange={() => selectedAttendees.length === filteredBookings.length ? setSelectedAttendees([]) : selectAllVisible()}
                      className="rounded border-[#D4AF37]"
                    />
                  </th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Guest</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Ticket #</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Booking Code</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Event</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Ticket</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Qty</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Amount</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Source</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Date</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((guest) => (
                  <tr key={guest.id} className="border-b border-[rgba(212,175,55,0.1)] hover:bg-[rgba(212,175,55,0.05)]">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedAttendees.includes(guest.id)}
                        onChange={() => toggleSelectAttendee(guest.id)}
                        className="rounded border-[#D4AF37]"
                      />
                    </td>
                    <td className="p-3">
                      <p className="text-[#F5F5F0] font-medium">{guest.name}</p>
                      <p className="text-[#A1A1AA] text-sm">{guest.email}</p>
                      {guest.phone && <p className="text-[#a1a1aa] text-xs">{guest.phone}</p>}
                    </td>
                    <td className="p-3">
                      {guest.id ? (
                        <span className="text-[#22C55E] font-mono text-sm font-bold" data-testid="guest-ticket-number" title="Ticket # on PDF">
                          {(guest.id || "").substring(0, 8).toUpperCase()}
                        </span>
                      ) : (
                        <span className="text-[#6B7280] text-xs">N/A</span>
                      )}
                    </td>
                    <td className="p-3">
                      {guest.booking_code ? (
                        <span className="text-[#D4AF37] font-mono text-sm" data-testid="guest-booking-code">{guest.booking_code}</span>
                      ) : (
                        <span className="text-[#6B7280] text-xs">N/A</span>
                      )}
                    </td>
                    <td className="p-3 text-[#F5F5F0]">{guest.event_title}</td>
                    <td className="p-3 text-[#A1A1AA]">{guest.ticket_type || "-"}</td>
                    <td className="p-3 text-[#A1A1AA]">{guest.quantity || 1}</td>
                    <td className="p-3 text-[#D4AF37]">C${(guest.total_amount || 0).toFixed(2)}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded ${
                        guest.source_type === "manual"
                          ? "bg-purple-900/30 text-purple-400"
                          : "bg-blue-900/30 text-blue-400"
                      }`} data-testid="guest-source-display">
                        {guest.source_display}
                      </span>
                    </td>
                    <td className="p-3 text-[#A1A1AA] text-sm">{formatDate(guest.created_at)}</td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingGuest(guest)}
                          className="p-2 bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 rounded transition-colors"
                          title="Edit guest"
                          data-testid="edit-guest-btn"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => window.location.href = `mailto:${guest.email}`}
                          className="p-2 bg-[rgba(212,175,55,0.1)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.2)] rounded"
                          title="Send email"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingGuest(guest)}
                          className="p-2 bg-red-900/30 text-red-400 hover:bg-red-900/50 rounded transition-colors"
                          title="Delete guest"
                          data-testid="delete-guest-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {editingGuest && (
        <EditGuestModal
          guest={editingGuest}
          onClose={() => setEditingGuest(null)}
          onSave={() => { fetchAllBookings(); setEditingGuest(null); }}
        />
      )}
      {deletingGuest && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0F0F0F] border border-red-900/50 w-full max-w-md p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-900/30 rounded">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]" data-testid="guest-delete-confirm-title">
                Delete Guest
              </h2>
            </div>
            <p className="text-[#A1A1AA] mb-2">
              Are you sure you want to delete this guest?
            </p>
            <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-3 mb-6 rounded">
              <p className="text-[#F5F5F0] font-medium">{deletingGuest.name}</p>
              <p className="text-[#A1A1AA] text-sm">{deletingGuest.email}</p>
              {deletingGuest.booking_code && (
                <p className="text-[#D4AF37] font-mono text-sm mt-1">{deletingGuest.booking_code}</p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setDeletingGuest(null)}
                className="btn-secondary flex-1"
                data-testid="guest-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteGuest(deletingGuest)}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
                data-testid="guest-delete-confirm"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
      {showEmailModal && (
        <EmailAttendeesModal
          attendees={filteredBookings.filter(a => selectedAttendees.includes(a.id))}
          onClose={() => setShowEmailModal(false)}
          onSent={() => { setShowEmailModal(false); setSelectedAttendees([]); }}
        />
      )}
    </AdminLayout>
  );
};

export default AdminGuestsPage;
